import { useState } from 'react';
import { usePlaybookSuggestions, generatePlaybookFromSuggestion, type PlaybookSuggestion } from '../../hooks/useSecOps';

function ConfidenceBar({ value }: { value: number }) {
  const color = value >= 75 ? '#16a34a' : value >= 50 ? '#2563eb' : value >= 25 ? '#d97706' : '#dc2626';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-100 rounded-full h-1.5">
        <div className="h-1.5 rounded-full" style={{ width: `${value}%`, backgroundColor: color }} />
      </div>
      <span className="text-[10px] font-medium" style={{ color }}>{value}%</span>
    </div>
  );
}

export default function PlaybookBuilderPage() {
  const { data: response, loading } = usePlaybookSuggestions();
  const suggestions = (response as { data?: { suggestions?: PlaybookSuggestion[]; total_incidents_analyzed?: number } })?.data?.suggestions ?? [];
  const totalAnalyzed = (response as { data?: { total_incidents_analyzed?: number } })?.data?.total_incidents_analyzed ?? 0;

  const [generating, setGenerating] = useState<string | null>(null);
  const [generated, setGenerated] = useState<Set<string>>(new Set());

  const handleGenerate = async (suggestion: PlaybookSuggestion) => {
    setGenerating(suggestion.name);
    try {
      await generatePlaybookFromSuggestion({
        name: suggestion.name,
        description: suggestion.description,
        classification: suggestion.classification,
        severity_trigger: suggestion.severity_trigger,
        steps: suggestion.common_steps,
      });
      setGenerated(prev => new Set(prev).add(suggestion.name));
    } finally {
      setGenerating(null);
    }
  };

  if (loading && suggestions.length === 0) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">AI Playbook Builder</h1>
        <div className="animate-pulse space-y-4">
          <div className="bg-gray-200 h-16 rounded-lg" />
          {[1, 2, 3].map(i => <div key={i} className="bg-gray-200 h-40 rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">AI Playbook Builder</h1>
        <p className="text-xs text-gray-400 mt-1">
          Analyzes incident patterns to suggest automated playbooks
          {totalAnalyzed > 0 && ` — ${totalAnalyzed} incidents analyzed`}
        </p>
      </div>

      {suggestions.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-sm text-gray-500 mb-2">Not enough incident data for pattern analysis</p>
          <p className="text-xs text-gray-400">Build up incident history (3+ incidents per classification) for AI-powered suggestions.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {suggestions.map((suggestion: PlaybookSuggestion, i: number) => (
            <div key={i} className="bg-white rounded-lg shadow p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-sm font-semibold text-gray-900">{suggestion.name}</h3>
                    <span className="text-[10px] px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full">
                      {suggestion.classification.replace(/_/g, ' ')}
                    </span>
                    <span className="text-[10px] text-gray-400">
                      Based on {suggestion.incident_count} incidents
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">{suggestion.description}</p>
                </div>
                <div className="ml-4">
                  {generated.has(suggestion.name) ? (
                    <span className="px-3 py-1.5 text-xs font-medium bg-green-100 text-green-700 rounded-lg">Created</span>
                  ) : (
                    <button
                      onClick={() => handleGenerate(suggestion)}
                      disabled={generating !== null}
                      className="px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {generating === suggestion.name ? 'Creating...' : 'Create Playbook'}
                    </button>
                  )}
                </div>
              </div>

              <div className="mb-3">
                <p className="text-[10px] text-gray-400 uppercase mb-1">AI Confidence</p>
                <ConfidenceBar value={suggestion.confidence} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] text-gray-400 uppercase mb-2">Suggested Steps</p>
                  <ol className="space-y-1">
                    {suggestion.common_steps.map((step: string, j: number) => (
                      <li key={j} className="flex items-start gap-2 text-xs text-gray-600">
                        <span className="text-[10px] font-medium text-indigo-500 mt-0.5">{j + 1}.</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 uppercase mb-2">Configuration</p>
                  <div className="space-y-1.5 text-xs text-gray-600">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">Triggers on:</span>
                      {suggestion.severity_trigger.map((s: string) => (
                        <span key={s} className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                          s === 'critical' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                        }`}>{s}</span>
                      ))}
                    </div>
                    <div>
                      <span className="text-gray-400">Est. duration:</span>{' '}
                      <span className="font-medium">{suggestion.estimated_duration_minutes}m</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
